import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { preventiviService } from '@/services/preventiviService';
import type {
  CreatePreventivoInput,
  InviaPreventivoInput,
  Paginated,
  PreventivoAnteprima,
  PreventivoCedolinoIncluso,
  PreventivoConfig,
  PreventivoConRelazioni,
  PreventivoFilters,
  PreventivoListItem,
  UpdatePreventivoInput,
  UUID,
} from '@/types';

function invalidatePreventivi(queryClient: ReturnType<typeof useQueryClient>, id?: UUID) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.preventivi.all });
  if (id) void queryClient.invalidateQueries({ queryKey: queryKeys.preventivi.detail(id) });
}

export function usePreventivi(filters: PreventivoFilters = {}): UseQueryResult<Paginated<PreventivoListItem>> {
  return useQuery({
    queryKey: queryKeys.preventivi.list(filters),
    queryFn: () => preventiviService.list(filters),
    meta: { resourceName: 'preventivi' },
  });
}

export function usePreventiviCestino(
  filters: PreventivoFilters = {},
  enabled = true,
): UseQueryResult<Paginated<PreventivoListItem>> {
  return useQuery({
    queryKey: queryKeys.preventivi.cestino(filters),
    queryFn: () => preventiviService.cestino(filters),
    enabled,
    meta: { resourceName: 'cestino preventivi' },
  });
}

export function useTrashPreventivo(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.trash(id),
    meta: { successMessage: 'Preventivo spostato nel cestino. I cedolini collegati sono di nuovo disponibili.' },
    onSuccess: () => {
      invalidatePreventivi(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.cedolini.all });
    },
  });
}

export function useRestorePreventivo(): UseMutationResult<PreventivoConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.restore(id),
    meta: { successMessage: 'Preventivo ripristinato.' },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
  });
}

export function usePurgePreventivo(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.purge(id),
    meta: { successMessage: 'Preventivo eliminato definitivamente.' },
    onSuccess: () => invalidatePreventivi(queryClient),
  });
}

export function usePreventivo(id: UUID | undefined): UseQueryResult<PreventivoConRelazioni> {
  return useQuery({
    queryKey: queryKeys.preventivi.detail(id ?? ''),
    queryFn: () => preventiviService.get(id as UUID),
    enabled: Boolean(id),
    meta: { resourceName: 'preventivo' },
  });
}

export function useCedoliniEligibili(clienteId: UUID | undefined): UseQueryResult<PreventivoCedolinoIncluso[]> {
  return useQuery({
    queryKey: queryKeys.preventivi.eligibili(clienteId ?? ''),
    queryFn: () => preventiviService.eligibili(clienteId as UUID),
    enabled: Boolean(clienteId),
    meta: { resourceName: 'cedolini eleggibili' },
  });
}

export function usePreventivoAnteprima(): UseMutationResult<PreventivoAnteprima, Error, CreatePreventivoInput> {
  return useMutation({
    mutationFn: (input) => preventiviService.anteprima(input),
    meta: { silentError: true },
  });
}

export function useCreatePreventivo(): UseMutationResult<PreventivoConRelazioni, Error, CreatePreventivoInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => preventiviService.create(input),
    meta: { successMessage: 'Preventivo generato.', silentError: true },
    onSuccess: (row, input) => {
      invalidatePreventivi(queryClient, row.id);
      if (input.lead_id) void queryClient.invalidateQueries({ queryKey: queryKeys.lead.all });
    },
  });
}

export function useUpdatePreventivo(): UseMutationResult<
  PreventivoConRelazioni,
  Error,
  { id: UUID; input: UpdatePreventivoInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) => preventiviService.update(id, input),
    meta: { successMessage: 'Preventivo aggiornato.', silentError: true },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
  });
}

export function useRigeneraPreventivo(): UseMutationResult<PreventivoConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.rigenera(id),
    meta: { successMessage: 'PDF rigenerato.' },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
  });
}

export function useInviaPreventivo(): UseMutationResult<
  PreventivoConRelazioni,
  Error,
  { id: UUID; input?: InviaPreventivoInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }) => {
      const row = await preventiviService.invia(id, input);
      if (row.email_errore) {
        invalidatePreventivi(queryClient, row.id);
        throw new Error(`Preventivo segnato come inviato, ma l’email non è partita. ${row.email_errore}`);
      }
      return row;
    },
    meta: { successMessage: 'Preventivo inviato.' },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
    onError: () => invalidatePreventivi(queryClient),
  });
}

export function useInviaPreventivoEmail(): UseMutationResult<PreventivoConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const row = await preventiviService.inviaEmail(id);
      if (row.email_errore) {
        invalidatePreventivi(queryClient, row.id);
        throw new Error(`Email non recapitata. ${row.email_errore}`);
      }
      return row;
    },
    meta: { successMessage: 'Email inviata al cliente.' },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
    onError: () => invalidatePreventivi(queryClient),
  });
}

export function useConvertiPreventivo(): UseMutationResult<PreventivoConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.converti(id),
    meta: { successMessage: 'Preventivo convertito. La fattura XML resta un passaggio successivo.' },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
  });
}

export function useConvertiFatturaPreventivo(): UseMutationResult<PreventivoConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.convertiFattura(id),
    meta: { successMessage: 'Preventivo segnato come fatturato.' },
    onSuccess: (row) => {
      invalidatePreventivi(queryClient, row.id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.cedolini.all });
    },
  });
}

export function useAnnullaPreventivoMensile(): UseMutationResult<PreventivoConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.annulla(id),
    meta: { successMessage: 'Preventivo annullato. I cedolini sono tornati disponibili.' },
    onSuccess: (row) => {
      invalidatePreventivi(queryClient, row.id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.cedolini.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.preventivi.daInviareCount });
    },
  });
}

export function useAccettaPreventivo(): UseMutationResult<PreventivoConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => preventiviService.accetta(id),
    meta: { successMessage: 'Preventivo accettato.' },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
  });
}

export function useContestaPreventivo(): UseMutationResult<PreventivoConRelazioni, Error, { id: UUID; motivo: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }) => preventiviService.contesta(id, motivo),
    meta: { successMessage: 'Contestazione inviata.' },
    onSuccess: (row) => invalidatePreventivi(queryClient, row.id),
  });
}

export function useBulkInviaPreventiviMensili(): UseMutationResult<
  { inviato: number; items: PreventivoConRelazioni[] },
  Error,
  { periodo_mese?: string; invia_email?: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => preventiviService.bulkInviaMensili(input),
    meta: { successMessage: 'Preventivi del mese inviati al cliente.' },
    onSuccess: () => {
      invalidatePreventivi(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.preventivi.daInviareCount });
    },
  });
}

export function usePreventiviDaInviareCount(enabled = true): UseQueryResult<{ count: number }> {
  return useQuery({
    queryKey: queryKeys.preventivi.daInviareCount,
    queryFn: () => preventiviService.daInviareCount(),
    enabled,
    meta: { resourceName: 'coda preventivi mensili' },
  });
}

export function usePreventivoConfig(): UseQueryResult<PreventivoConfig> {
  return useQuery({
    queryKey: queryKeys.preventivi.config,
    queryFn: () => preventiviService.getConfig(),
    meta: { resourceName: 'intestazione preventivo' },
  });
}

export function useUpdatePreventivoConfig(): UseMutationResult<PreventivoConfig, Error, PreventivoConfig> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => preventiviService.updateConfig(input),
    meta: { successMessage: 'Intestazione aggiornata. I prossimi PDF useranno questi dati.', silentError: true },
    onSuccess: (row) => {
      queryClient.setQueryData(queryKeys.preventivi.config, row);
    },
  });
}
